/**
 * Date/Time Utility Functions
 * ===========================
 * 
 * Centralized utility functions for consistent date/time handling across the app.
 * All functions assume input dates/times are in UTC format and convert them to user's local time.
 */

/**
 * Converts a UTC datetime string to a local Date object
 * @param {string} utcDateTimeString - UTC datetime string (e.g., "2025-01-15 14:30:00" or "2025-01-15T14:30:00Z")
 * @returns {Date|null} - Local Date object or null if invalid
 */
export const convertUTCToLocal = (utcDateTimeString) => {
  if (!utcDateTimeString) return null;
  
  try {
    // Handle the format "2025-01-15 14:30:00" by adding 'Z' to indicate UTC
    const utcString = utcDateTimeString.includes('Z') ? utcDateTimeString : `${utcDateTimeString}Z`;
    const date = new Date(utcString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', utcDateTimeString);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('Error converting UTC to local:', error);
    return null;
  }
};

/**
 * Formats a UTC datetime string to local time (e.g., "3:45 PM")
 * @param {string} utcDateTimeString - UTC datetime string
 * @returns {string} - Formatted time string or 'N/A' if invalid
 */
export const formatTime = (utcDateTimeString) => {
  const date = convertUTCToLocal(utcDateTimeString);
  if (!date) return 'N/A';
  
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Formats a UTC datetime string to local date (e.g., "Wednesday, June 18, 2025")
 * @param {string} utcDateTimeString - UTC datetime string
 * @returns {string} - Formatted date string or 'N/A' if invalid
 */
export const formatDate = (utcDateTimeString) => {
  const date = convertUTCToLocal(utcDateTimeString);
  if (!date) return 'N/A';
  
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Formats a UTC datetime string to local date and time (e.g., "Jun 18, 2025, 3:45 PM")
 * @param {string} utcDateTimeString - UTC datetime string
 * @returns {string} - Formatted datetime string or 'N/A' if invalid
 */
export const formatDateTime = (utcDateTimeString) => {
  const date = convertUTCToLocal(utcDateTimeString);
  if (!date) return 'N/A';
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Formats a UTC datetime string to short date (e.g., "Jun 18, 2025")
 * @param {string} utcDateTimeString - UTC datetime string
 * @returns {string} - Formatted short date string or 'N/A' if invalid
 */
export const formatShortDate = (utcDateTimeString) => {
  const date = convertUTCToLocal(utcDateTimeString);
  if (!date) return 'N/A';
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Formats a UTC datetime string to compact date and time (e.g., "Jun 18, 3:45 PM")
 * @param {string} utcDateTimeString - UTC datetime string
 * @returns {string} - Formatted compact datetime string or 'N/A' if invalid
 */
export const formatCompactDateTime = (utcDateTimeString) => {
  const date = convertUTCToLocal(utcDateTimeString);
  if (!date) return 'N/A';
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Converts local date and time to UTC format for API calls
 * @param {string} localDate - Local date string (YYYY-MM-DD)
 * @param {string} localTime - Local time string (HH:MM AM/PM)
 * @returns {string} - UTC datetime string in format "YYYY-MM-DD HH:MM:SS"
 */
export const convertLocalToUTC = (localDate, localTime) => {
  if (!localDate || !localTime) return null;
  
  try {
    // Parse the local date and time
    const [year, month, day] = localDate.split('-').map(Number);
    
    // Parse time string (e.g., "3:45 PM")
    const timeMatch = localTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!timeMatch) {
      console.warn('Invalid time format:', localTime);
      return null;
    }
    
    let hours = parseInt(timeMatch[1]);
    const minutes = parseInt(timeMatch[2]);
    const ampm = timeMatch[3].toUpperCase();
    
    // Convert to 24-hour format
    if (ampm === 'PM' && hours !== 12) {
      hours += 12;
    } else if (ampm === 'AM' && hours === 12) {
      hours = 0;
    }
    
    // Create a local Date; toISOString() returns the corresponding UTC instant.
    const localDateTime = new Date(year, month - 1, day, hours, minutes, 0);

    // Format as "YYYY-MM-DD HH:MM:SS" in UTC
    const iso = localDateTime.toISOString(); // already UTC
    const utcDateString = iso.slice(0, 10);
    const utcTimeString = iso.slice(11, 19);

    return `${utcDateString} ${utcTimeString}`;
  } catch (error) {
    console.error('Error converting local to UTC:', error);
    return null;
  }
};

/**
 * Gets current local date in YYYY-MM-DD format
 * @returns {string} - Current local date string
 */
export const getCurrentLocalDate = () => {
  // Use local calendar date (toISOString is UTC and can shift the day)
  return new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
};

/**
 * Gets current local time in HH:MM AM/PM format
 * @returns {string} - Current local time string
 */
export const getCurrentLocalTime = () => {
  return new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};
